import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';

interface EnvironmentInfo {
  ambientColor: { r: number; g: number; b: number } | null;
  ambientColorHex: string | null;
  defaultCamera?: {
    type: string;
    offset: { x: number; y: number; z: number } | null;
    smoothing: number;
    fov: number;
  } | null;
  activeCameraTrigger?: string | null;
  activeCamera?: {
    name: string;
    position: { x: number; y: number; z: number };
    target: { x: number; y: number; z: number };
  } | null;
  skybox?: {
    loaded: boolean;
    name?: string;
    type?: string;
    envIntensity?: number;
    skyboxBrightness?: number;
    rotation?: number;
    textureName?: string;
    hdrUrl?: string | null;
  } | null;
  fog?: {
    enabled: boolean;
    density: number;
    color: { r: number; g: number; b: number };
  } | null;
}

/**
 * Environment Panel
 * 
 * Displays and edits system-wide environment settings.
 * Ambient and fog colors are editable; other values are read-only.
 */
@customElement('environment-panel')
export class EnvironmentPanel extends LitElement {
  @state() private envInfo: EnvironmentInfo = {
    ambientColor: null,
    ambientColorHex: null,
    defaultCamera: null,
    activeCameraTrigger: null,
    activeCamera: null,
    skybox: null,
  };
  
  // Local editable state for ambient color
  @state() private ambientR = 0;
  @state() private ambientG = 0;
  @state() private ambientB = 0;
  
  // Local editable state for fog
  @state() private fogEnabled = false;
  @state() private fogDensity = 0.02;
  @state() private fogColorR = 0.5;
  @state() private fogColorG = 0.5;
  @state() private fogColorB = 0.6;
  
  // Local editable state for HDR
  @state() private hdrUrl = '';
  @state() private envIntensity = 1.0;  // IBL/reflection intensity
  @state() private skyboxBrightness = 0.5;  // Visible skybox brightness
  
  static styles = css`
    :host {
      display: block;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 16px;
    }
    
    .section {
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .section:last-of-type {
      border-bottom: none;
    }
    
    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      opacity: 0.95;
    }
    
    .info-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 8px 0;
      font-size: 12px;
    }
    
    .info-row .label {
      min-width: 120px;
      opacity: 0.75;
    }
    
    .info-row .value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      opacity: 0.95;
      flex: 1;
    }
    
    .color-swatch {
      width: 32px;
      height: 20px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 3px;
      display: inline-block;
    }
    
    .note {
      margin-top: 12px;
      padding: 8px;
      background: rgba(100, 160, 255, 0.1);
      border-left: 3px solid rgba(100, 160, 255, 0.5);
      font-size: 11px;
      opacity: 0.8;
      border-radius: 3px;
    }
    
    .empty {
      opacity: 0.5;
      font-style: italic;
      font-size: 12px;
    }
    
    .slider-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 8px 0;
    }
    
    .slider-row .label {
      min-width: 80px;
      opacity: 0.75;
      font-size: 12px;
    }
    
    input[type="range"] {
      flex: 1;
      height: 24px;
      cursor: pointer;
    }
    
    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
    
    .value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      min-width: 40px;
      text-align: right;
      opacity: 0.95;
    }
    
    label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
      cursor: pointer;
      user-select: none;
    }
    
    label:hover {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      padding-left: 4px;
    }
    
    input[type="color"] {
      width: 48px;
      height: 28px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 4px;
      cursor: pointer;
      background: transparent;
      padding: 2px;
    }
    
    input[type="color"]::-webkit-color-swatch-wrapper {
      padding: 0;
    }
    
    input[type="color"]::-webkit-color-swatch {
      border: none;
      border-radius: 2px;
    }
    
    .color-picker-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 8px 0;
    }
    
    .color-picker-row .label {
      min-width: 80px;
      opacity: 0.75;
      font-size: 12px;
    }
    
    .color-picker-row .hex-value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      opacity: 0.95;
    }
    
    input[type="text"] {
      flex: 1;
      padding: 6px 8px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
    }
    
    input[type="text"]:focus {
      outline: none;
      border-color: rgba(100, 160, 255, 0.7);
    }
    
    input[type="text"]::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
    
    .button {
      padding: 6px 12px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 4px;
      background: rgba(100, 160, 255, 0.2);
      color: white;
      font-size: 12px;
      cursor: pointer;
      transition: background 0.15s;
    }
    
    .button:hover {
      background: rgba(100, 160, 255, 0.4);
    }
    
    .button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .input-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 8px 0;
    }
  `;
  
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
    
    // Request initial environment state
    DebugUiBridge.dispatchCommand({ type: 'environment:refresh' } as any);
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }
  
  private onDebugEvent = (e: CustomEvent) => {
    const event = e.detail;
    
    if (event.type === 'environment:info') {
      this.envInfo = event.info;
      // Sync local ambient color state
      if (event.info.ambientColor) {
        this.ambientR = event.info.ambientColor.r;
        this.ambientG = event.info.ambientColor.g;
        this.ambientB = event.info.ambientColor.b;
      }
      // Sync fog state
      if (event.info.fog) {
        this.fogEnabled = event.info.fog.enabled;
        this.fogDensity = event.info.fog.density;
        this.fogColorR = event.info.fog.color.r;
        this.fogColorG = event.info.fog.color.g;
        this.fogColorB = event.info.fog.color.b;
      }
      // Sync HDR state
      if (event.info.skybox) {
        if (event.info.skybox.hdrUrl) {
          this.hdrUrl = event.info.skybox.hdrUrl;
        }
        if (event.info.skybox.envIntensity !== undefined) {
          this.envIntensity = event.info.skybox.envIntensity;
        }
        if (event.info.skybox.skyboxBrightness !== undefined) {
          this.skyboxBrightness = event.info.skybox.skyboxBrightness;
        }
      }
    } else if (event.type === 'environment:ambient:changed') {
      this.ambientR = event.color.r;
      this.ambientG = event.color.g;
      this.ambientB = event.color.b;
      // Update envInfo too
      this.envInfo = { ...this.envInfo, ambientColor: event.color };
    } else if (event.type === 'environment:fog:changed') {
      this.fogEnabled = event.enabled;
      this.fogDensity = event.density ?? this.fogDensity;
      if (event.color) {
        this.fogColorR = event.color.r;
        this.fogColorG = event.color.g;
        this.fogColorB = event.color.b;
      }
    } else if (event.type === 'environment:hdr:changed') {
      this.hdrUrl = event.url ?? '';
      if (event.envIntensity !== undefined) this.envIntensity = event.envIntensity;
      if (event.skyboxBrightness !== undefined) this.skyboxBrightness = event.skyboxBrightness;
      // Update envInfo skybox with new HDR
      this.envInfo = {
        ...this.envInfo,
        skybox: {
          ...this.envInfo.skybox,
          loaded: !!event.url,
          hdrUrl: event.url,
          envIntensity: event.envIntensity,
          skyboxBrightness: event.skyboxBrightness
        }
      };
    }
  };
  
  private rgbToHex(r: number, g: number, b: number): string {
    const toHex = (n: number) => {
      const hex = Math.round(n * 255).toString(16).padStart(2, '0');
      return hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  
  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16) / 255,
      g: parseInt(result[2], 16) / 255,
      b: parseInt(result[3], 16) / 255
    } : { r: 0, g: 0, b: 0 };
  }
  
  // Ambient color handlers
  private onAmbientColorPick(e: Event) {
    const hex = (e.target as HTMLInputElement).value;
    const rgb = this.hexToRgb(hex);
    this.ambientR = rgb.r;
    this.ambientG = rgb.g;
    this.ambientB = rgb.b;
    this.dispatchAmbientColor();
  }
  
  private onAmbientRChange(e: Event) {
    this.ambientR = parseFloat((e.target as HTMLInputElement).value);
    this.dispatchAmbientColor();
  }
  
  private onAmbientGChange(e: Event) {
    this.ambientG = parseFloat((e.target as HTMLInputElement).value);
    this.dispatchAmbientColor();
  }
  
  private onAmbientBChange(e: Event) {
    this.ambientB = parseFloat((e.target as HTMLInputElement).value);
    this.dispatchAmbientColor();
  }
  
  private dispatchAmbientColor() {
    DebugUiBridge.dispatchCommand({
      type: 'environment:ambient:color:set',
      r: this.ambientR,
      g: this.ambientG,
      b: this.ambientB
    });
  }
  
  // Fog handlers
  private toggleFog() {
    this.fogEnabled = !this.fogEnabled;
    DebugUiBridge.dispatchCommand({ type: 'environment:fog:set', enabled: this.fogEnabled });
  }
  
  // Convert linear slider (0-1) to quadratic density (0-0.2)
  // This gives more resolution in the 0-0.05 range
  private sliderToDensity(sliderValue: number): number {
    return sliderValue * sliderValue * 0.2;
  }
  
  // Convert density to linear slider value
  private densityToSlider(density: number): number {
    return Math.sqrt(density / 0.2);
  }
  
  private onFogDensityChange(e: Event) {
    const sliderValue = parseFloat((e.target as HTMLInputElement).value);
    this.fogDensity = this.sliderToDensity(sliderValue);
    DebugUiBridge.dispatchCommand({ type: 'environment:fog:density:set', value: this.fogDensity });
  }
  
  private onFogColorPick(e: Event) {
    const hex = (e.target as HTMLInputElement).value;
    const rgb = this.hexToRgb(hex);
    this.fogColorR = rgb.r;
    this.fogColorG = rgb.g;
    this.fogColorB = rgb.b;
    this.dispatchFogColor();
  }
  
  private onFogColorRChange(e: Event) {
    this.fogColorR = parseFloat((e.target as HTMLInputElement).value);
    this.dispatchFogColor();
  }
  
  private onFogColorGChange(e: Event) {
    this.fogColorG = parseFloat((e.target as HTMLInputElement).value);
    this.dispatchFogColor();
  }
  
  private onFogColorBChange(e: Event) {
    this.fogColorB = parseFloat((e.target as HTMLInputElement).value);
    this.dispatchFogColor();
  }
  
  private dispatchFogColor() {
    DebugUiBridge.dispatchCommand({
      type: 'environment:fog:color:set',
      r: this.fogColorR,
      g: this.fogColorG,
      b: this.fogColorB
    });
  }
  
  // HDR handlers
  private onHdrUrlInput(e: Event) {
    this.hdrUrl = (e.target as HTMLInputElement).value;
  }
  
  private loadHdr() {
    if (!this.hdrUrl) return;
    DebugUiBridge.dispatchCommand({
      type: 'environment:hdr:load',
      url: this.hdrUrl,
      envIntensity: this.envIntensity,
      skyboxBrightness: this.skyboxBrightness
    });
  }
  
  private onEnvIntensityChange(e: Event) {
    this.envIntensity = parseFloat((e.target as HTMLInputElement).value);
    DebugUiBridge.dispatchCommand({
      type: 'environment:hdr:envIntensity:set',
      value: this.envIntensity
    });
  }
  
  private onSkyboxBrightnessChange(e: Event) {
    this.skyboxBrightness = parseFloat((e.target as HTMLInputElement).value);
    DebugUiBridge.dispatchCommand({
      type: 'environment:hdr:skyboxBrightness:set',
      value: this.skyboxBrightness
    });
  }
  
  render() {
    const ambientHex = this.rgbToHex(this.ambientR, this.ambientG, this.ambientB);
    const fogHex = this.rgbToHex(this.fogColorR, this.fogColorG, this.fogColorB);
    
    return html`
      <div class="section">
        <h3>Ambient Lighting</h3>
        <div class="color-picker-row">
          <span class="label">Color</span>
          <input
            type="color"
            .value=${ambientHex}
            @input=${this.onAmbientColorPick}
          />
          <span class="hex-value">${ambientHex}</span>
        </div>
        <div class="slider-row">
          <span class="label">Red</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.ambientR)}
            @input=${this.onAmbientRChange}
          />
          <span class="value">${this.ambientR.toFixed(2)}</span>
        </div>
        <div class="slider-row">
          <span class="label">Green</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.ambientG)}
            @input=${this.onAmbientGChange}
          />
          <span class="value">${this.ambientG.toFixed(2)}</span>
        </div>
        <div class="slider-row">
          <span class="label">Blue</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.ambientB)}
            @input=${this.onAmbientBChange}
          />
          <span class="value">${this.ambientB.toFixed(2)}</span>
        </div>
      </div>
      
      <div class="section">
        <h3>Fog</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.fogEnabled}
            @change=${this.toggleFog}
          />
          Enable Fog
        </label>
        
        <div class="slider-row">
          <span class="label">Density</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.densityToSlider(this.fogDensity))}
            @input=${this.onFogDensityChange}
            ?disabled=${!this.fogEnabled}
          />
          <span class="value">${this.fogDensity.toFixed(4)}</span>
        </div>
        
        <div class="color-picker-row">
          <span class="label">Color</span>
          <input
            type="color"
            .value=${fogHex}
            @input=${this.onFogColorPick}
            ?disabled=${!this.fogEnabled}
          />
          <span class="hex-value">${fogHex}</span>
        </div>
        <div class="slider-row">
          <span class="label">Red</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.fogColorR)}
            @input=${this.onFogColorRChange}
            ?disabled=${!this.fogEnabled}
          />
          <span class="value">${this.fogColorR.toFixed(2)}</span>
        </div>
        <div class="slider-row">
          <span class="label">Green</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.fogColorG)}
            @input=${this.onFogColorGChange}
            ?disabled=${!this.fogEnabled}
          />
          <span class="value">${this.fogColorG.toFixed(2)}</span>
        </div>
        <div class="slider-row">
          <span class="label">Blue</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.fogColorB)}
            @input=${this.onFogColorBChange}
            ?disabled=${!this.fogEnabled}
          />
          <span class="value">${this.fogColorB.toFixed(2)}</span>
        </div>
      </div>
      
      <div class="section">
        <h3>Active Camera</h3>
        ${this.envInfo.activeCamera ? html`
          <div class="info-row">
            <span class="label">Name:</span>
            <span class="value">${this.envInfo.activeCamera.name}</span>
          </div>
          <div class="info-row">
            <span class="label">Position:</span>
            <span class="value">
              (${this.envInfo.activeCamera.position.x.toFixed(2)}, ${this.envInfo.activeCamera.position.y.toFixed(2)}, ${this.envInfo.activeCamera.position.z.toFixed(2)})
            </span>
          </div>
          <div class="info-row">
            <span class="label">Target:</span>
            <span class="value">
              (${this.envInfo.activeCamera.target.x.toFixed(2)}, ${this.envInfo.activeCamera.target.y.toFixed(2)}, ${this.envInfo.activeCamera.target.z.toFixed(2)})
            </span>
          </div>
        ` : html`
          <div class="empty">No active camera</div>
        `}
      </div>
      
      <div class="section">
        <h3>Default Camera Config</h3>
        ${this.envInfo.defaultCamera ? html`
          <div class="info-row">
            <span class="label">Type:</span>
            <span class="value">${this.envInfo.defaultCamera.type}</span>
          </div>
          ${this.envInfo.defaultCamera.offset ? html`
            <div class="info-row">
              <span class="label">Offset:</span>
              <span class="value">
                (${this.envInfo.defaultCamera.offset.x.toFixed(2)}, ${this.envInfo.defaultCamera.offset.y.toFixed(2)}, ${this.envInfo.defaultCamera.offset.z.toFixed(2)})
              </span>
            </div>
          ` : ''}
          <div class="info-row">
            <span class="label">Smoothing:</span>
            <span class="value">${this.envInfo.defaultCamera.smoothing.toFixed(2)}</span>
          </div>
          <div class="info-row">
            <span class="label">FOV:</span>
            <span class="value">${this.envInfo.defaultCamera.fov}°</span>
          </div>
          ${this.envInfo.activeCameraTrigger ? html`
            <div class="info-row">
              <span class="label">Active Trigger:</span>
              <span class="value">${this.envInfo.activeCameraTrigger}</span>
            </div>
          ` : ''}
        ` : html`
          <div class="empty">No default camera configured</div>
        `}
      </div>
      
      <div class="section">
        <h3>Skybox / Environment</h3>
        
        <div class="input-row">
          <input
            type="text"
            placeholder="HDR URL (.env or .hdr)"
            .value=${this.hdrUrl}
            @input=${this.onHdrUrlInput}
          />
          <button
            class="button"
            @click=${this.loadHdr}
            ?disabled=${!this.hdrUrl}
          >Load</button>
        </div>
        
        <div class="slider-row">
          <span class="label">Env IBL</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.envIntensity)}
            @input=${this.onEnvIntensityChange}
          />
          <span class="value">${this.envIntensity.toFixed(2)}</span>
        </div>
        
        <div class="slider-row">
          <span class="label">Sky</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.skyboxBrightness)}
            @input=${this.onSkyboxBrightnessChange}
          />
          <span class="value">${this.skyboxBrightness.toFixed(2)}</span>
        </div>
        
        ${this.envInfo.skybox?.loaded ? html`
          <div class="info-row">
            <span class="label">Name:</span>
            <span class="value">${this.envInfo.skybox.name || 'N/A'}</span>
          </div>
          <div class="info-row">
            <span class="label">Type:</span>
            <span class="value">${this.envInfo.skybox.type || 'skybox'}</span>
          </div>
          ${this.envInfo.skybox.textureName ? html`
            <div class="info-row">
              <span class="label">Texture:</span>
              <span class="value">${this.envInfo.skybox.textureName}</span>
            </div>
          ` : ''}
          ${this.envInfo.skybox.hdrUrl ? html`
            <div class="info-row">
              <span class="label">HDR:</span>
              <span class="value">${this.envInfo.skybox.hdrUrl}</span>
            </div>
          ` : ''}
          ${this.envInfo.skybox.rotation !== undefined && this.envInfo.skybox.rotation !== 0 ? html`
            <div class="info-row">
              <span class="label">Rotation:</span>
              <span class="value">${this.envInfo.skybox.rotation}°</span>
            </div>
          ` : ''}
        ` : html`
          <div class="empty">No skybox/environment loaded</div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'environment-panel': EnvironmentPanel;
  }
}
